import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useLogger, ANALYTICS_EVENTS } from "@/features/analytics"
import { CreditsPurchaseModal } from "@/features/advisors"
import type { Advisor } from "@/features/advisors"
import { openAppModal, shouldShowReviewModal } from "@/features/app-modals"

import { useChat } from "../hooks/use-chat"
import { useChatTimer } from "../hooks/use-chat-timer"
import { ASSISTANT_STATUSES, MESSAGE_ROLE } from "../types"
import { chattingCopy } from "../config/chat-copy"
import { ChatMessages } from "./chat-messages"
import { ChatInput } from "./chat-input"
import { LeaveChatModal } from "./leave-chat-modal"

import ClockIcon from "@/assets/img/live-chat/clock-purple.svg?react"
import CloseIcon from "@/assets/img/live-chat/close-icon-purple.svg?react"

interface ChatConversationProps {
  selectedAdvisor: Advisor
}

export function ChatConversation({ selectedAdvisor }: ChatConversationProps) {
  const {
    user,
    setUnreadAdvisorIds,
    setSelectedAdvisor,
    sendMessage,
    setMessages,
    messages,
    assistantStatuses,
    userCreditsCount,
    emitEnterChatEvent,
    emitLeaveChatEvent,
    userMainSubscriptionId,
    preSelectedChatMessage,
    setPreSelectedChatMessage,
  } = useChat()
  const { trackAppyCntEvent } = useLogger()

  const isChatAvailable = useMemo(
    () => userCreditsCount !== null && userCreditsCount > 0,
    [userCreditsCount],
  )
  const { chatTimeUi } = useChatTimer({ isCounting: isChatAvailable })

  const [value, setValue] = useState("")
  const [isLeaveModalOpen, setLeaveModalOpen] = useState(false)
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)
  // Suppress the out-of-credits auto-reopen right after a successful purchase,
  // until the polled balance actually reflects the new credits (avoids the
  // modal instantly reopening while userCreditsCount is still 0).
  const [creditsJustPurchased, setCreditsJustPurchased] = useState(false)
  const [pendingReply, setPendingReply] = useState(false)
  const messagesRef = useRef<HTMLDivElement>(null)

  const currentAssistantStatus = useMemo(
    () =>
      assistantStatuses.find((el) => el.assistantId === selectedAdvisor.assistantId)?.status ??
      ASSISTANT_STATUSES.COMPLETED,
    [assistantStatuses, selectedAdvisor.assistantId],
  )

  const isTyping = pendingReply || currentAssistantStatus === ASSISTANT_STATUSES.PROCESSING

  const handleSendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return

      setMessages((prev) => [...prev, { role: MESSAGE_ROLE.USER, content: trimmed }])
      setValue("")
      setPendingReply(true)
      // The backend now paces the reply (typed-out, multi-bubble), so emit right
      // away instead of holding the message behind an artificial client delay.
      sendMessage(trimmed)
    },
    [setMessages, sendMessage],
  )

  // Stop the typing indicator once the assistant replies.
  useEffect(() => {
    if (messages[messages.length - 1]?.role === MESSAGE_ROLE.ASSISTANT) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingReply(false)
    }
  }, [messages])

  // Out of credits -> paywall (unless we just purchased and await the balance refresh).
  useEffect(() => {
    if (userCreditsCount === 0 && !creditsJustPurchased) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsPaywallOpen(true)
      trackAppyCntEvent(ANALYTICS_EVENTS.PAYWALL_SHOW, undefined, {
        user: { uid: user?.uid, name: user?.displayName, host: window.location.host },
      })
    }
  }, [userCreditsCount, creditsJustPurchased, trackAppyCntEvent, user])

  // Once the purchased credits land, clear the suppression so a later depletion
  // can open the paywall again.
  useEffect(() => {
    if (userCreditsCount !== null && userCreditsCount > 0 && creditsJustPurchased) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCreditsJustPurchased(false)
    }
  }, [userCreditsCount, creditsJustPurchased])

  // Clear this advisor's unread marker on open.
  useEffect(() => {
    setUnreadAdvisorIds((prev) => prev.filter((id) => id !== selectedAdvisor.assistantId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-send the preset question carried over from the advisors page.
  useEffect(() => {
    if (preSelectedChatMessage) {
      /* eslint-disable react-hooks/set-state-in-effect */
      handleSendMessage(preSelectedChatMessage)
      setPreSelectedChatMessage(null)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedChatMessage])

  useEffect(() => {
    if (isChatAvailable) emitEnterChatEvent()
    else emitLeaveChatEvent()
  }, [isChatAvailable, emitEnterChatEvent, emitLeaveChatEvent])

  useEffect(() => {
    return () => emitLeaveChatEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeChat = () => {
    setSelectedAdvisor(null)
    emitLeaveChatEvent()
    trackAppyCntEvent(ANALYTICS_EVENTS.CHAT_WITH_ADVISOR_END, undefined, {
      user: { uid: user?.uid, name: user?.displayName, host: window.location.host },
      advisor: { name: selectedAdvisor.name, id: selectedAdvisor.assistantId },
    })
    if (shouldShowReviewModal()) {
      openAppModal("review")
    }
  }

  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && value.trim()) handleSendMessage(value)
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-0 z-[100] mx-auto flex max-w-app flex-col bg-dark-base">
      {/* Header */}
      <div className="flex h-14 items-center border-b border-purple-light/10 px-5">
        <img
          src={selectedAdvisor.photo}
          alt={selectedAdvisor.name}
          className="mr-2 size-10 rounded-full object-cover"
        />
        <div className="flex flex-col items-start">
          <span className="font-display text-base font-medium text-white">
            {selectedAdvisor.name}
          </span>
          <span className="flex items-center gap-1 font-body text-xs text-green">
            <span className="size-1.5 rounded-full bg-green" />
            Online
          </span>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-1.5 rounded-[20px] bg-purple-light/10 px-3 py-2 [&_svg]:size-5">
            <ClockIcon />
            <span className="font-body text-xs text-purple-light">{chatTimeUi}</span>
          </div>
          <button
            type="button"
            onClick={() => setLeaveModalOpen(true)}
            className="flex [&_svg]:size-6"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ChatMessages messages={messages} isTyping={isTyping} scrollRef={messagesRef} />
        <ChatInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onSend={() => handleSendMessage(value)}
          onKeyUp={onKeyUp}
          onPreset={(preset) => handleSendMessage(preset)}
          placeholder={chattingCopy.message}
          isActive={value.trim().length > 0}
        />
      </div>

      <LeaveChatModal
        isOpen={isLeaveModalOpen}
        onKeep={() => setLeaveModalOpen(false)}
        onLeave={closeChat}
        onPreset={(preset) => {
          setLeaveModalOpen(false)
          handleSendMessage(preset)
        }}
      />

      <CreditsPurchaseModal
        isOpen={isPaywallOpen}
        onClose={() => setIsPaywallOpen(false)}
        onPurchased={() => {
          setCreditsJustPurchased(true)
          setIsPaywallOpen(false)
        }}
        userMainSubscriptionId={userMainSubscriptionId}
        variant="chat-page"
      />
    </div>
  )
}
